/**
 * Auth port (IMPL-03, client side). Login is Firebase Identity Platform, but the app depends only
 * on this small port so the real SDK is swappable for a fake in tests and so an unconfigured build
 * degrades to local-only (anonymous) mode instead of crashing. The session yields a *fresh* ID
 * token on demand; that token is the Bearer the API and sync layers verify server-side.
 */
export type AuthProviderId = 'google' | 'apple';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface AuthSession {
  user: AuthUser;
  /** A current (auto-refreshed) Identity Platform ID token. */
  getIdToken(): Promise<string>;
}

export interface AuthClient {
  /** True when real auth is configured; false → local-only mode (sign-in disabled). */
  readonly configured: boolean;
  /**
   * Subscribe to auth-state changes. Fires once with the current state on subscribe (null until
   * resolved). Returns an unsubscribe function.
   */
  onChange(cb: (session: AuthSession | null) => void): () => void;
  signIn(provider: AuthProviderId): Promise<void>;
  signOut(): Promise<void>;
  /**
   * E2E-only: sign in with a pre-minted Firebase custom token. Optional because only the real
   * Firebase client implements it, and it's reachable only from the VITE_E2E build's window hook —
   * never wired in a production bundle. Lets Playwright authenticate without the Google OAuth popup.
   */
  signInWithToken?(token: string): Promise<void>;
}

/**
 * Local-only client for builds without Firebase configured. The app still runs (offline, single
 * local identity); sign-in surfaces a clear error rather than silently doing nothing.
 */
export const localOnlyAuthClient: AuthClient = {
  configured: false,
  onChange(cb) {
    cb(null);
    return () => {};
  },
  async signIn() {
    throw new Error('Sign-in is unavailable: Firebase is not configured (set VITE_FIREBASE_* env).');
  },
  async signOut() {
    /* nothing to do */
  },
};

/**
 * Deterministic in-memory client for tests. `signIn` immediately produces a user whose token is
 * `id-<provider>-<uid>`; `onChange` notifies all subscribers on every transition.
 */
export class FakeAuthClient implements AuthClient {
  readonly configured = true;
  private session: AuthSession | null = null;
  private subs = new Set<(s: AuthSession | null) => void>();
  private seq = 0;

  constructor(private readonly emailFor: (p: AuthProviderId) => string = (p) => `user@${p}.test`) {}

  onChange(cb: (s: AuthSession | null) => void): () => void {
    this.subs.add(cb);
    cb(this.session);
    return () => this.subs.delete(cb);
  }

  async signIn(provider: AuthProviderId): Promise<void> {
    const uid = `${provider}-uid-${this.seq++}`;
    const email = this.emailFor(provider);
    this.session = {
      user: { uid, email, displayName: email.split('@')[0] ?? null },
      getIdToken: async () => `id-${provider}-${uid}`,
    };
    this.emit();
  }

  async signOut(): Promise<void> {
    this.session = null;
    this.emit();
  }

  private emit(): void {
    for (const cb of this.subs) cb(this.session);
  }
}
